import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Flag, X, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import { submitFeedback, track } from "../lib/api";
import { useChat } from "../lib/chatContext";

/**
 * FeedbackButton — small "דווח על בעיה" link that opens a tiny modal,
 * captures (a) the user's message, (b) the current page path, and
 * (c) the latest user-message context chip (e.g. SKU 195) so the admin
 * can reproduce the report immediately.
 *
 * Submissions land in the `feedback` Mongo collection and are visible in
 * the Admin Dashboard under "פידבק מהמטמיע".
 */
export default function FeedbackButton({ className = "" }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const taRef = useRef(null);
  const location = useLocation();
  const { messages } = useChat();

  useEffect(() => {
    if (open && taRef.current) {
      setTimeout(() => taRef.current?.focus(), 50);
    }
  }, [open]);

  // Pick the freshest context chip the user saw in the rail — usually the
  // SKU / PO / family they were inspecting when something looked off.
  const lastContext = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "user" && messages[i].context?.label) {
        return messages[i].context;
      }
    }
    return null;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const msg = text.trim();
    if (!msg) {
      toast.error("אי אפשר לשלוח פידבק ריק");
      return;
    }
    setSending(true);
    try {
      await submitFeedback(msg, location.pathname, lastContext);
      track("feedback_submit", location.pathname, { len: msg.length });
      toast.success("תודה! הפידבק הגיע לאדמין.");
      setText("");
      setOpen(false);
    } catch (err) {
      toast.error("שגיאה: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center justify-center gap-1.5 text-[11px] text-zinc-500 hover:text-amber-300 transition-colors ${className}`}
        data-testid="feedback-open-btn"
        title="דווח על בעיה / רעיון לשיפור — נשמר אצל האדמין"
      >
        <Flag className="w-3 h-3" />
        <span>דווח על בעיה</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[80] flex items-center justify-center px-4"
            data-testid="feedback-modal"
          >
            <div
              className="absolute inset-0 bg-black/75 backdrop-blur-sm"
              onClick={() => !sending && setOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.97 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-md bg-card border border-border rounded-lg shadow-2xl overflow-hidden"
              dir="rtl"
            >
              {/* Header */}
              <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
                <div className="w-7 h-7 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                  <Flag className="w-3.5 h-3.5 text-amber-300" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-foreground">דווח על בעיה</div>
                </div>
                <button
                  type="button"
                  onClick={() => !sending && setOpen(false)}
                  className="text-zinc-500 hover:text-foreground p-1"
                  aria-label="סגור"
                  data-testid="feedback-close-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Body */}
              <form onSubmit={handleSubmit} className="p-4 space-y-3">
                {/* Auto-context info */}
                <div className="space-y-1.5 text-[11px]">
                  <div className="flex items-center gap-2 text-zinc-500">
                    <span className="text-zinc-600">דף:</span>
                    <code className="font-mono text-zinc-300">{location.pathname}</code>
                  </div>
                  {lastContext?.label && (
                    <div className="flex items-center gap-2 text-zinc-500">
                      <span className="text-zinc-600">הקשר:</span>
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 border border-primary/25 text-primary font-mono"
                        data-testid="feedback-context-chip"
                      >
                        {lastContext.label}
                        {lastContext.sublabel && (
                          <span className="text-primary/60">· {lastContext.sublabel}</span>
                        )}
                      </span>
                    </div>
                  )}
                </div>

                <textarea
                  ref={taRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="מה ראית? למה זה לא ברור / לא עובד? מה היית מצפה לראות במקום?"
                  className="w-full h-28 bg-[#0A0A0E] border border-border rounded-md p-3 text-sm text-foreground placeholder-zinc-600 focus:outline-none focus:border-primary/50 transition-colors resize-none"
                  disabled={sending}
                  maxLength={4000}
                  data-testid="feedback-textarea"
                />
                <div className="flex items-center justify-between text-[10px] text-zinc-600 font-mono">
                  <span className="flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    <span>נשלח כ-{text.length > 0 ? "מטמיע" : "טיוטה"}</span>
                  </span>
                  <span>{text.length} / 4000</span>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => !sending && setOpen(false)}
                    className="text-xs text-zinc-400 hover:text-foreground px-3 py-2"
                    data-testid="feedback-cancel-btn"
                  >
                    ביטול
                  </button>
                  <button
                    type="submit"
                    disabled={sending || !text.trim()}
                    className="inline-flex items-center gap-1.5 text-xs text-primary-foreground bg-primary hover:bg-primary/90 disabled:bg-zinc-700 disabled:text-zinc-500 disabled:cursor-not-allowed px-3 py-2 rounded-md transition-all"
                    data-testid="feedback-submit-btn"
                  >
                    <Check className="w-3 h-3" />
                    <span>{sending ? "שולח..." : "שלח לאדמין"}</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
